import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Table } from "react-bootstrap";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const EngTable1 = ({ data }) => {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p className="text-center text-gray-500">No data available</p>;
  }

  const years = data.map((item) => item.year);

  // Chart Data
  const chartData = {
    labels: years,
    datasets: [
      {
        label: "All Sexes",
        data: data.map((item) => item.all_sexes_growth),
        backgroundColor: "#ffffbb",
        borderRadius: 6,
      },
      {
        label: "Female",
        data: data.map((item) => item.female_growth),
        backgroundColor: "#EC4899",
        borderRadius: 6,
      },
      {
        label: "Male",
        data: data.map((item) => item.male_growth),
        backgroundColor: "#0ea5e9",
        borderRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employment Growth by Sex (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%` },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year" } },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Growth (%)" },
        ticks: { callback: (v) => `${v}%` },
      },
    },
  };

  // 📈 Summary Insights
  const totalGrowth = (
    ((data[data.length - 1].all_sexes - data[0].all_sexes) /
      data[0].all_sexes) *
    100
  ).toFixed(1);

  const peakYear = data.reduce(
    (max, item) =>
      item.all_sexes > max.value
        ? { year: item.year, value: item.all_sexes }
        : max,
    { year: data[0].year, value: data[0].all_sexes },
  );

  const recentYear = data[data.length - 1];

  // 📄 PDF Download
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text(APP_VERSION, 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("employment_growth_by_sex.pdf");
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-lg"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Growth by Sex (2018–2024)
        </h2>

        {/* 📊 Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Overall Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-blue-800">+{totalGrowth}%</p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Peak Employment Year
            </p>
            <p className="text-xl font-bold text-yellow-800">{peakYear.year}</p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              2024 Employment Snapshot
            </p>
            <p className="text-lg font-bold text-green-800">
              All Sexes: {Number(recentYear.all_sexes).toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* 📅 Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col h-full overflow-x-auto">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2 text-left">Year</th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    All Sexes
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    Female
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    Male
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    All Sexes Growth (%)
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    Female Growth (%)
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    Male Growth (%)
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr
                    key={row.year}
                    className={`${
                      row.year === 2024
                        ? "bg-green-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2 text-left">{row.year}</td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {Number(row.all_sexes).toLocaleString("en-US")}
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {Number(row.female).toLocaleString("en-US")}
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {Number(row.male).toLocaleString("en-US")}
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.all_sexes_growth || "N/A"}%
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.female_growth || "N/A"}%
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.male_growth || "N/A"}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* 🧾 Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable1;

